'use client';

import { useState } from 'react';

import { Textarea } from '@holistic/components';
import { Smartphone } from '@holistic/components/icons';

import FlowCard from './flow-card';
import IdentityNumberField from './identity-number-field';
import SerialNumberField from './serial-number-field';
import { useWarrantySuggestion } from './use-warranty-suggestion';
import WarrantyPickerModal from './warranty-picker-modal';
import WarrantySelectedBanner from './warranty-selected-banner';
import WarrantySuggestion from './warranty-suggestion';

type DeviceInfoCardProps = {
  description: string;
  onDescriptionChange: (v: string) => void;
  serialNumber: string;
  onSerialNumberChange: (v: string) => void;
  serialNumberError: string | null;
  identityNumber: string;
  onIdentityNumberChange: (v: string) => void;
  identityNumberError: string | null;
  warrantyId: string | null;
  onWarrantySelect: (warrantyId: string | null, serialNumber?: string) => void;
  isLoggedIn: boolean;
};

/**
 * Cihaz bilgileri (seri no, TCKN, açıklama ve garanti eşleştirme).
 */
const DeviceInfoCard = ({
  description,
  onDescriptionChange,
  serialNumber,
  onSerialNumberChange,
  serialNumberError,
  identityNumber,
  onIdentityNumberChange,
  identityNumberError,
  warrantyId,
  onWarrantySelect,
  isLoggedIn,
}: DeviceInfoCardProps) => {
  const [showPicker, setShowPicker] = useState(false);
  const {
    warranty: suggested,
    remainingDays,
    dismiss,
  } = useWarrantySuggestion(serialNumber, isLoggedIn);

  return (
    <>
      <FlowCard
        icon={Smartphone}
        title="Cihaz ve Müşteri Bilgileri"
        bodyClassName="flex flex-col gap-5 p-5"
      >
        {/* Misafir akışında TCKN "Hesap" kartında, diğer kimlik alanlarının
            yanında sorulur; burada tekrar göstermiyoruz. */}
        {isLoggedIn && (
          <IdentityNumberField
            value={identityNumber}
            onChange={onIdentityNumberChange}
            error={identityNumberError}
          />
        )}

        <SerialNumberField
          value={serialNumber}
          onChange={onSerialNumberChange}
          error={serialNumberError}
          onPickFromWarranties={
            warrantyId || !isLoggedIn ? null : () => setShowPicker(true)
          }
        />

        {warrantyId ? (
          <WarrantySelectedBanner onRemove={() => onWarrantySelect(null)} />
        ) : (
          suggested && (
            <WarrantySuggestion
              warranty={suggested}
              remainingDays={remainingDays}
              onAccept={() =>
                onWarrantySelect(suggested.warrantyId, suggested.serialNumber)
              }
              onDismiss={dismiss}
            />
          )
        )}

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            Açıklama <span className="text-gray-400">(opsiyonel)</span>
          </label>
          <Textarea
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            placeholder="Cihazındaki sorunu kısaca anlat..."
            rows={4}
          />
        </div>
      </FlowCard>

      {showPicker && (
        <WarrantyPickerModal
          onSelect={onWarrantySelect}
          onClose={() => setShowPicker(false)}
        />
      )}
    </>
  );
};

export default DeviceInfoCard;
